// trendLineTool.js — shared "Trend Line" click-drag multi-line tool for both charts
// (Dashboard's SignalChart.js and the More Info chart in App.js). First of the four
// "advanced drawing" tools (Trend Line, Parallel Channel, Fib Retracement, Long
// Position) planned in the 2026-07-08 handoff — this build scopes ONLY Trend Line;
// the other three are separate future builds against the same chart_advanced_drawings
// table (migration 0007).
//
// Interaction pattern is measureTool.js's click-drag (mousedown → mousemove → mouseup),
// NOT Draw Line's single-click-to-place — a trend line needs two arbitrary (time, price)
// points, not one price on a vertical click. Persistence pattern is drawingsStore.js's
// LIST semantics (multiple trend lines coexist per symbol/context, each independently
// deletable), NOT measurementsStore.js's single-overwritable-row semantics. This file
// intentionally duplicates measureTool.js's pointFromEvent / pan-zoom-disable /
// re-projection-on-pan-zoom logic rather than importing from it — the two hooks'
// mouseup branches (overwrite-one vs. append-to-list) diverge enough that sharing
// would need a parameterized abstraction not worth building for a 2nd caller.
//
// Anchoring: identical to measureTool.js — points stored in DATA coordinates (time +
// price), re-projected to pixels every render, re-triggered on pan/zoom via a
// visible-range subscription, so lines stay pinned to their candles.

import { useState, useRef, useEffect, useCallback } from "react";
import { fetchAdvancedDrawings, insertAdvancedDrawing, deleteAdvancedDrawing } from "./advancedDrawingsStore";

const TYPE = "trendline";

// chartRef / seriesRef: refs to the lightweight-charts chart instance + price series.
// containerRef: ref to the chart's DOM container (for mouse event coordinates + listeners).
// userId/symbol/context: identify which saved rows to read/write (context: "dashboard" | "more_info").
export function useTrendLineTool({ chartRef, seriesRef, containerRef, userId, symbol, context }) {
  const [trendLineMode, setTrendLineMode] = useState(false);
  const [myLines, setMyLines] = useState([]); // [{id, time1, price1, time2, price2}] — DATA space
  const [draft, setDraft] = useState(null);   // in-progress drag, DATA space, not yet persisted
  const [, setRangeTick] = useState(0);        // bumped on pan/zoom so pixel projection recomputes
  const draggingRef = useRef(false);
  const draftRef = useRef(null); // mirrors draft — read inside mouseup to avoid stale closures

  // Load this user/symbol/context's saved trend lines whenever any of those change.
  useEffect(() => {
    let cancelled = false;
    setMyLines([]);
    if (!userId || !symbol || !context) return;
    fetchAdvancedDrawings(userId, symbol, context, TYPE)
      .then((rows) => {
        if (cancelled) return;
        setMyLines(rows.map((r) => ({ id: r.id, ...r.points })));
      })
      .catch((e) => console.error("[chart_advanced_drawings-read]", e?.message || e));
    return () => { cancelled = true; };
  }, [userId, symbol, context]);

  const pointFromEvent = useCallback((e) => {
    const container = containerRef.current;
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!container || !chart || !series) return null;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const price = series.coordinateToPrice(y);
    const time = chart.timeScale().coordinateToTime(x);
    if (price == null || time == null) return null;
    return { price, time };
  }, [chartRef, seriesRef, containerRef]);

  // Click-drag effect — armed while trendLineMode is on. Stays armed across multiple
  // drags (multi-line mode, like Draw Line) so the user can place several trend lines
  // in a row; only disarms when they click "Trend Line" again to turn it off.
  useEffect(() => {
    const container = containerRef.current;
    if (!trendLineMode || !container || !userId) return;

    const handleMouseDown = (e) => {
      const p = pointFromEvent(e);
      if (!p) return;
      draggingRef.current = true;
      chartRef.current?.applyOptions({ handleScroll: false, handleScale: false });
      const next = { time1: p.time, price1: p.price, time2: p.time, price2: p.price };
      draftRef.current = next;
      setDraft(next);
    };
    const handleMouseMove = (e) => {
      if (!draggingRef.current) return;
      const p = pointFromEvent(e);
      if (!p) return;
      const prev = draftRef.current;
      if (!prev) return;
      const next = { ...prev, time2: p.time, price2: p.price };
      draftRef.current = next;
      setDraft(next);
    };
    const handleMouseUp = () => {
      const wasDragging = draggingRef.current;
      draggingRef.current = false;
      chartRef.current?.applyOptions({ handleScroll: true, handleScale: true });
      if (!wasDragging) return; // stray mouseup elsewhere on the page — nothing to finish
      const finished = draftRef.current;
      draftRef.current = null;
      setDraft(null);
      // Ignore a zero-distance click (no real drag) — avoids persisting a degenerate line.
      if (!finished || (finished.time1 === finished.time2 && finished.price1 === finished.price2)) return;
      insertAdvancedDrawing(userId, symbol, context, TYPE, finished)
        .then((row) => setMyLines((prev) => [...prev, { id: row.id, ...row.points }]))
        .catch((e) => console.error("[chart_advanced_drawings-insert]", e?.message || e));
      // Deliberately does NOT disarm — multi-line mode, same as Draw Line.
    };

    container.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      // Safety: always restore normal pan/zoom when the tool is disarmed/unmounted,
      // even if a drag was interrupted mid-flight.
      chartRef.current?.applyOptions({ handleScroll: true, handleScale: true });
    };
  }, [trendLineMode, containerRef, chartRef, pointFromEvent, userId, symbol, context]);

  const deleteTrendLine = useCallback((id) => {
    if (!userId) return;
    deleteAdvancedDrawing(userId, id)
      .then(() => setMyLines((prev) => prev.filter((l) => l.id !== id)))
      .catch((e) => console.error("[chart_advanced_drawings-delete]", e?.message || e));
  }, [userId]);

  // Re-project on pan/zoom — WATCHDOG version (2026-07-09). The old one-shot
  // subscription silently died when the chart instance wasn't created yet (drawings
  // fetch racing chart creation on refresh) or was recreated on refetch — drawings
  // then sat mis-calibrated until an unrelated render. This attaches to the CURRENT
  // chart, re-attaches on instance swap, and fires an immediate tick on attach so
  // saved drawings calibrate instantly.
  useEffect(() => {
    if (myLines.length === 0 && !draft) return;
    const handler = () => setRangeTick((t) => t + 1);
    let subscribed = null;
    const ensure = () => {
      const chart = chartRef.current;
      if (chart === subscribed) return;
      if (subscribed) { try { subscribed.timeScale().unsubscribeVisibleLogicalRangeChange(handler); } catch (e) { /* torn down */ } }
      subscribed = chart || null;
      if (chart) {
        try { chart.timeScale().subscribeVisibleLogicalRangeChange(handler); handler(); } catch (e) { subscribed = null; }
      }
    };
    ensure();
    const watchdog = setInterval(ensure, 400);
    return () => {
      clearInterval(watchdog);
      if (subscribed) { try { subscribed.timeScale().unsubscribeVisibleLogicalRangeChange(handler); } catch (e) { /* torn down */ } }
    };
  }, [chartRef, myLines.length, draft]);

  // Derived: project every saved line + the in-progress draft (if any) from data space
  // to pixel space, recomputed every render (not memoized — same convention as
  // measureTool.js's single-measurement projection).
  const projectedLines = [];
  const chart = chartRef.current;
  const series = seriesRef.current;
  if (chart && series) {
    const project = (pts, id) => {
      const x1 = chart.timeScale().timeToCoordinate(pts.time1);
      const x2 = chart.timeScale().timeToCoordinate(pts.time2);
      const y1 = series.priceToCoordinate(pts.price1);
      const y2 = series.priceToCoordinate(pts.price2);
      if (x1 == null || x2 == null || y1 == null || y2 == null) return null;
      return { id, x1, y1, x2, y2 };
    };
    myLines.forEach((l) => {
      const p = project(l, l.id);
      if (p) projectedLines.push(p);
    });
    if (draft) {
      const p = project(draft, null);
      if (p) projectedLines.push({ ...p, isDraft: true });
    }
  }

  return { trendLineMode, setTrendLineMode, myLines, deleteTrendLine, projectedLines };
}

// SVG overlay — one line per saved (or in-progress) trend line. Same absolutely-positioned
// sibling-of-container pattern as MeasureOverlay, zIndex 5, pointerEvents "none" on the
// root (see measureTool.js's MeasureOverlay comment for why that's critical — without it
// the overlay's full pane-covering bounding box eats every mouse event on the chart).
// No stats box (unlike Measure) — this is a pure visual anchor tool, not a readout.
// Orange (#f97316) matches Draw Line's color convention for this app's drawing tools;
// the in-progress draft renders at reduced opacity so it reads as "not yet placed."
export function TrendLineOverlay({ lines }) {
  if (!lines || lines.length === 0) return null;
  return (
    <svg
      style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 5, pointerEvents: "none" }}
    >
      {lines.map((l, i) => (
        <line
          key={l.id ?? `draft-${i}`}
          x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
          stroke="#f97316"
          strokeOpacity={l.isDraft ? 0.5 : 1}
          strokeWidth="1.5"
        />
      ))}
    </svg>
  );
}
